import React, { useMemo } from "react";
import { generatePath, useLocation, useNavigate, useParams } from "react-router-dom";
import {
    useGetLanguageVersionsForMenuFillingQuery,
    useGetLanguageVersionsForMenuQuery,
} from "@me-team/host/main/menuLanguageVersions/graphql/menuLanguageVersions.hooks";
import { useMenuLanguageVersion } from "../../utils/hooks/useMenuLanguageVersion";
import MenuLanguageVersionSelect from "./MenuLanguageVersionSelect";
import { useTranslation } from "react-i18next";
import { WatchQueryFetchPolicy } from "@apollo/client";

const ADD_LANGUAGE_VALUE = "__ADD_LANGUAGE__";

interface MenuLanguageVersionSelectContainerProps {
    companyId: number;
    /** For create pages: show primary and disable changing */
    isLockedToPrimary?: boolean;
    onRequestLanguageChange?: (nextLanguageCode: string) => void;
    fetchPolicy?: WatchQueryFetchPolicy;
    showAddLanguageOption?: boolean;
    menuFillingContext?: {
        branchId: number;
        menuId: number;
    };
}

const MenuLanguageVersionSelectContainer: React.FC<MenuLanguageVersionSelectContainerProps> = ({
    companyId,
    isLockedToPrimary,
    onRequestLanguageChange,
    fetchPolicy = "cache-and-network",
    showAddLanguageOption = true,
    menuFillingContext,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { branchId } = useParams();

    const hasMenuFillingContext = useMemo(() => {
        return Boolean(
            menuFillingContext &&
                Number.isFinite(menuFillingContext.branchId) &&
                Number.isFinite(menuFillingContext.menuId)
        );
    }, [menuFillingContext]);

    const { data: companyLanguageVersionsData, loading: companyLanguageVersionsLoading } =
        useGetLanguageVersionsForMenuQuery({
            variables: { companyId },
            skip: !companyId || hasMenuFillingContext,
            context: { errorType: "local" },
            fetchPolicy,
        });

    const { data: menuFillingLanguageVersionsData, loading: menuFillingLanguageVersionsLoading } =
        useGetLanguageVersionsForMenuFillingQuery({
            variables: {
                companyId,
                branchId: menuFillingContext?.branchId,
                menuId: menuFillingContext?.menuId,
            },
            skip: !companyId || !hasMenuFillingContext,
            context: { errorType: "local" },
            fetchPolicy,
        });

    const loading = hasMenuFillingContext ? menuFillingLanguageVersionsLoading : companyLanguageVersionsLoading;

    const primaryLanguage = hasMenuFillingContext
        ? menuFillingLanguageVersionsData?.user?.company?.branches?.[0]?.menusList?.[0]?.primaryLanguage
        : companyLanguageVersionsData?.user?.company?.primaryLanguage;

    const languages = useMemo(() => {
        if (hasMenuFillingContext) {
            return menuFillingLanguageVersionsData?.user?.company?.branches?.[0]?.menusList?.[0]?.languages ?? [];
        }

        return companyLanguageVersionsData?.user?.company?.languages ?? [];
    }, [
        companyLanguageVersionsData?.user?.company?.languages,
        hasMenuFillingContext,
        menuFillingLanguageVersionsData?.user?.company?.branches,
    ]);

    const primaryLanguageCode = primaryLanguage?.isoCode ?? null;
    const primaryLanguageName = primaryLanguage?.name?.trim() ?? null;

    const availableLanguageCodes = useMemo(() => {
        const codes = languages
            .map((language) => language?.isoCode?.trim())
            .filter((isoCode): isoCode is string => Boolean(isoCode));

        if (primaryLanguageCode && !codes.includes(primaryLanguageCode)) {
            codes.unshift(primaryLanguageCode);
        }

        return codes;
    }, [languages, primaryLanguageCode]);

    const { currentLanguageCode, setMenuLanguageVersion } = useMenuLanguageVersion({
        primaryLanguageCode,
        availableLanguageCodes,
        isLockedToPrimary,
    });

    const addLanguagePath = useMemo(() => {
        if (!branchId) return null;

        return generatePath("/company/:currentCompanyId/edit/:branchId", {
            currentCompanyId: String(companyId),
            branchId,
        });
    }, [branchId, companyId]);

    const handleChangeValue = (nextValue: string) => {
        if (!nextValue) return;

        if (nextValue === ADD_LANGUAGE_VALUE) {
            if (!addLanguagePath) return;

            navigate(addLanguagePath, {
                state: {
                    from: `${location.pathname}${location.search}`,
                    flow: "add_menu_language",
                    focus: "menu_languages",
                },
            });
            return;
        }

        if (isLockedToPrimary) return;

        if (onRequestLanguageChange) {
            onRequestLanguageChange(nextValue);
            return;
        }

        setMenuLanguageVersion(nextValue);
    };

    const hasData = hasMenuFillingContext
        ? Boolean(menuFillingLanguageVersionsData)
        : Boolean(companyLanguageVersionsData);

    const isLoading = loading && !hasData;

    const isDisabled: boolean = isLockedToPrimary || !primaryLanguageCode;

    return (
        <MenuLanguageVersionSelect
            languages={languages}
            primaryLanguageCode={primaryLanguageCode}
            primaryLanguageName={primaryLanguageName}
            currentLanguageCode={currentLanguageCode}
            addLanguageValue={ADD_LANGUAGE_VALUE}
            addLanguageLabel={t("Add language")}
            showAddLanguageOption={showAddLanguageOption}
            primaryLabel={t("primary")}
            onChangeValue={handleChangeValue}
            isDisabled={isDisabled}
            isLoading={isLoading}
        />
    );
};

export default React.memo(MenuLanguageVersionSelectContainer);
